import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Candidate, PollResponse, Precinct, QRDocument, User, Voter } from '../../types';
import React, { useEffect, useState } from 'react';
import { Timestamp, addDoc, collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref, uploadBytes } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router-dom';

import PrecinctVoterFilterDialog from './PrecinctVoterFilterDialog';
import PrecinctVoterTableRow from './PrecinctVoterTableRow';
import { generateCombinedVoterPDF } from '../../utils/pdfUtils';
import useAuth from '../../hooks/useAuth';

const PrecinctDetailsSkeleton: React.FC = () => {
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" width={150} height={40} />
      </Box>

      <Paper elevation={3} sx={{ mb: 3, p: 2 }}>
        <Skeleton variant="text" width={200} height={30} />
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          {[1, 2, 3, 4].map((item) => (
            <Skeleton key={item} variant="text" width="100%" height={25} />
          ))}
        </Box>
      </Paper>

      <Box display="flex" justifyContent="flex-start" mb={2} gap={2}>
        <Skeleton variant="rectangular" width={200} height={40} />
        <Skeleton variant="rectangular" width={100} height={40} />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {['', '#', 'Name', 'ID', 'QR Documents'].map((header, index) => (
                <TableCell key={index}>
                  <Skeleton variant="text" width={header ? 80 : 30} height={25} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5].map((row) => (
              <TableRow key={row}>
                {[1, 2, 3, 4, 5].map((cell, index) => (
                  <TableCell key={cell}>
                    <Skeleton variant="text" width={index === 0 ? 30 : 100} height={25} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const PrecinctDetails: React.FC = () => {
  const { barangayId, precinctNumber } = useParams<{ barangayId: string; precinctNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [precinct, setPrecinct] = useState<Precinct | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [voters, setVoters] = useState<Voter[]>([]);
  const [filteredVoters, setFilteredVoters] = useState<Voter[]>([]);
  const [selectedVoters, setSelectedVoters] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // New state for filtering
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{
    votedCandidate: string;
    qrDocumentStatus: 'generated' | 'not_generated' | 'all';
  }>({
    votedCandidate: '',
    qrDocumentStatus: 'all'
  });

  useEffect(() => {
    const fetchPrecinctDetails = async () => {
      try {
        // Fetch precinct details
        const precinctRef = collection(db, 'precincts');
        const precinctQuery = query(
          precinctRef, 
          where('barangay_id', '==', barangayId),
          where('precinct', '==', precinctNumber)
        );
        
        const precinctSnapshot = await getDocs(precinctQuery);
        
        if (!precinctSnapshot.empty) {
          const precinctData = precinctSnapshot.docs[0].data() as Precinct;
          setPrecinct(precinctData);

          // Fetch candidates
          const candidatesRef = collection(db, 'candidates');
          const candidatesSnapshot = await getDocs(candidatesRef);
          const fetchedCandidates = candidatesSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              type: data.type || 'provincial',
              color: data.color || '',
              name: data.name || '',
              position: data.position || ''
            } as Candidate;
          });
          setCandidates(fetchedCandidates);

          // Fetch voters for this precinct
          const votersRef = collection(db, 'voters');
          const votersQuery = query(
            votersRef, 
            where('precinct', '==', precinctData.precinct),
            where('barangay', '==', precinctData.barangay),
            where('city_municipality', '==', precinctData.city_municipality),
            orderBy('name')
          );

          const voterSnapshot = await getDocs(votersQuery);
          
          const fetchedVoters = voterSnapshot.docs.map(doc => ({
            ...doc.data() as Voter,
            id: doc.id
          }));

          setVoters(fetchedVoters);
          setFilteredVoters(fetchedVoters);
        }
      } catch (error) {
        console.error('Error fetching precinct details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrecinctDetails();
  }, [barangayId, precinctNumber]);

  // New method to check QR document status
  const checkQRDocumentStatus = async (voter: Voter) => {
    try {
      const qrDocumentsRef = collection(db, 'qr_documents');
      const qrQuery = query(
        qrDocumentsRef, 
        where('voters', 'array-contains', { id: voter.id, name: voter.name })
      );
      const qrSnapshot = await getDocs(qrQuery);
      return !qrSnapshot.empty;
    } catch (error) {
      console.error('Error checking QR document status:', error);
      return false;
    }
  };

  // New method to check voted candidates
  const checkVotedCandidates = async (user: User, voter: Voter) => {
    try {
      if (!precinct || candidates.length === 0) {
        return [];
      }

      const pollResponsePath = 
        `tabulationsV2/${user.dashboard}/regions/${precinct.region}/`
        + `provinces/${precinct.province}/districts/${precinct.district}/`
        + `cities/${precinct.city_municipality}/barangays/${precinct.barangay}/`
        + `precincts/${precinct.precinct}/voters/${voter.name}`;

      const latestPollResponseDocRef = doc(db, pollResponsePath);
      const latestPollResponseDocSnap = await getDoc(latestPollResponseDocRef);

      const votedCandidates: string[] = [];
      if (latestPollResponseDocSnap.exists()) {
        const latestPoll = latestPollResponseDocSnap.data() as PollResponse;
        
        for (const [question, candidateResponse] of Object.entries(latestPoll.responses)) {
          if (candidateResponse) {
            if (Array.isArray(candidateResponse)) {
              for (const response of candidateResponse) {
                if (typeof response === 'string') {
                  votedCandidates.push(response);
                }
              }
            } else if (typeof candidateResponse === 'string') {
              votedCandidates.push(candidateResponse);
            }
          }
        }
      }

      return votedCandidates;
    } catch (error) {
      console.error('Error checking voted candidates:', error);
      return [];
    }
  };

  // New method to apply filters
  const applyFilters = async (user: User) => {
    setLoading(true);
    let filtered = [...voters];

    // Filter by QR document status
    if (activeFilters.qrDocumentStatus !== 'all') {
      const promises = filtered.map(async (voter) => {
        const hasQRDocument = await checkQRDocumentStatus(voter);
        if (activeFilters.qrDocumentStatus === 'generated') {
          return hasQRDocument ? voter : null;
        } else if (activeFilters.qrDocumentStatus === 'not_generated') {
          return hasQRDocument ? null : voter;
        }
      });
      const results = await Promise.all(promises);
      filtered = results.filter((voter) => voter !== null) as Voter[];
    }

    // Filter by voted candidates
    if (activeFilters.votedCandidate) {
      const promises = filtered.map(async (voter) => {
        const votedCandidates = await checkVotedCandidates(user, voter);
        return votedCandidates.includes(activeFilters.votedCandidate) ? voter : null;
      });
      const results = await Promise.all(promises);
      filtered = results.filter((voter) => voter !== null) as Voter[];
    }

    setFilteredVoters(filtered);
    setLoading(false);
  };

  useEffect(() => {
    if (user && voters.length > 0 && precinct && candidates.length > 0) {
      applyFilters(user);
    }
  }, [user, activeFilters, voters, precinct, candidates]);

  const handleGenerateQRDocuments = async () => {
    if (precinct && selectedVoters.length > 0 && user && !isGenerating) {
      try {
        // Set generating state to prevent multiple generations
        setIsGenerating(true);

        // Filter voters based on selected voter IDs
        const selectedVotersList = filteredVoters.filter(voter => selectedVoters.includes(voter.id));

        // Generate combined PDF with selected voters list and envelope pages
        const pdfBlob = await generateCombinedVoterPDF(
          selectedVotersList, 
          precinct.precinct, 
          precinct.barangay, 
          precinct.city_municipality
        );

        // Create a unique filename
        const timestamp = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
        const filename = `qr_document_${precinct.precinct}_${precinct.barangay}_${precinct.city_municipality}_${timestamp}.pdf`;
        
        // Upload PDF to Firebase Storage
        const storageRef = ref(storage, `qr_documents/${filename}`);
        const uploadResult = await uploadBytes(storageRef, pdfBlob);

        // Prepare QR Document for Firestore
        const qrDocument: QRDocument = {
          filename: filename,
          storagePath: uploadResult.ref.fullPath,
          voters: selectedVotersList.map(voter => ({ id: voter.id, name: voter.name })),
          timestamp: Timestamp.now(),
          updatedBy: user.display_name ?? user.email ?? user.uid ?? 'anonymous'
        };

        // Save QR Document to Firestore
        await addDoc(collection(db, 'qr_documents'), qrDocument);

        // trigger download
        const link = document.createElement('a');
        link.href = URL.createObjectURL(pdfBlob);
        link.download = filename;
        link.click();

        console.log('QR Document generated and saved:', qrDocument);
      } catch (error) {
        console.error('Error generating QR documents:', error);
      } finally {
        // Reset generating state
        setIsGenerating(false);
      }
    }
  };

  const handleVoterSelect = (voterId: string) => {
    setSelectedVoters(prev => 
      prev.includes(voterId) 
        ? prev.filter(id => id !== voterId) 
        : [...prev, voterId]
    );
  };

  const handleSelectAllVoters = () => {
    if (selectedVoters.length === filteredVoters.length) {
      // If all are currently selected, deselect all
      setSelectedVoters([]);
    } else {
      // Select all filtered voter IDs
      setSelectedVoters(filteredVoters.map(voter => voter.id));
    }
  };

  // New method to render active filters
  const renderActiveFilters = () => {
    const filters: { label: string, onDelete: () => void }[] = [];

    if (activeFilters.votedCandidate) {
      filters.push({
        label: `Candidate: ${activeFilters.votedCandidate}`, 
        onDelete: () => setActiveFilters(prev => ({ ...prev, votedCandidate: '' }))
      });
    }

    if (activeFilters.qrDocumentStatus !== 'all') {
      filters.push({
        label: `QR Status: ${activeFilters.qrDocumentStatus === 'generated' ? 'Generated' : 'Not Generated'}`, 
        onDelete: () => setActiveFilters(prev => ({ ...prev, qrDocumentStatus: 'all' }))
      });
    }

    return filters.length > 0 ? (
      <Box mb={2}>
        <Typography variant="subtitle1" gutterBottom>
          Active Filters
        </Typography>
        <Stack direction="row" spacing={1}>
          {filters.map((filter, index) => (
            <Chip 
              key={index} 
              label={filter.label} 
              onDelete={filter.onDelete}
            />
          ))}
          {filters.length > 1 && (
            <Chip 
              label="Clear All" 
              onDelete={() => setActiveFilters({ votedCandidate: '', qrDocumentStatus: 'all' })}
              color="primary"
            />
          )}
        </Stack>
      </Box>
    ) : null;
  };

  if (loading) {
    return <PrecinctDetailsSkeleton />;
  }
  
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Precinct Details: {precinct?.precinct}
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/precincts')}
        >
          Back to Precinct List
        </Button>
      </Box>

      <Paper elevation={3} sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6">Precinct Information</Typography>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <Typography><strong>Barangay:</strong> {precinct?.barangay}</Typography>
          <Typography><strong>City/Municipality:</strong> {precinct?.city_municipality}</Typography>
          <Typography><strong>Province:</strong> {precinct?.province}</Typography>
          <Typography><strong>District:</strong> {precinct?.district}</Typography>
        </Box>
      </Paper>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleGenerateQRDocuments}
          disabled={selectedVoters.length === 0 || isGenerating}
        >
          {isGenerating ? 'Generating...' : 'Generate QR Documents'}
        </Button>
        <Button 
          variant="outlined"
          onClick={() => setFilterDialogOpen(true)}
        >
          Filter
        </Button>
      </Box>

      {renderActiveFilters()}
      
      <Typography variant="subtitle1" gutterBottom>
        Total: {filteredVoters.length}
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedVoters.length > 0 && selectedVoters.length < filteredVoters.length}
                  checked={filteredVoters.length > 0 && selectedVoters.length === filteredVoters.length}
                  onChange={handleSelectAllVoters}
                />
              </TableCell>
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>QR Documents</TableCell>
              <TableCell>Stub Released</TableCell>
              <TableCell>Final Released</TableCell>
              <TableCell>Exit Polled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVoters.map((voter, index) => (
              <PrecinctVoterTableRow 
                key={voter.id}
                voter={voter}
                index={index}
                isSelected={selectedVoters.includes(voter.id)}
                onSelectVoter={handleVoterSelect}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredVoters.length === 0 && (
        <Typography align="center" mt={2}>
          No voters found matching the current filters
        </Typography>
      )}

      {precinct && (
        <PrecinctVoterFilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          onApplyFilter={(filters) => {
            setActiveFilters(filters);
            setFilterDialogOpen(false);
          }}
          precinct={{
            precinct: precinct.precinct,
            barangay: precinct.barangay,
            city_municipality: precinct.city_municipality
          }}
        />
      )}
    </Box>
  );
};

export default PrecinctDetails;
